/**
 * Command Palette - Phase 11 Enhanced
 * Global search and navigation with Cmd+K / Ctrl+K
 * Supports: stock search, page navigation, AI queries
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  TrendingUp,
  FileText,
  Clock,
  ArrowRight,
  CornerDownLeft,
  Sparkles
} from 'lucide-react'
import { apiClient } from '../api/client'
import { useUIStore } from '../stores/uiStore'
import './CommandPalette.css'

interface SearchResult {
  ticker: string
  company_name: string
  sector?: string
  current_price?: number
  change_pct?: number
}

interface PageResult {
  path: string
  name: string
  category: string
  keywords?: string[]
}

// Common tickers for instant results
const COMMON_TICKERS: Record<string, string> = {
  'AAPL': 'Apple Inc.',
  'MSFT': 'Microsoft Corporation',
  'GOOGL': 'Alphabet Inc.',
  'AMZN': 'Amazon.com Inc.',
  'NVDA': 'NVIDIA Corporation',
  'TSLA': 'Tesla Inc.',
  'META': 'Meta Platforms Inc.',
  'JPM': 'JPMorgan Chase & Co.',
  'V': 'Visa Inc.',
  'JNJ': 'Johnson & Johnson',
  'WMT': 'Walmart Inc.',
  'PG': 'Procter & Gamble Co.',
  'MA': 'Mastercard Inc.',
  'UNH': 'UnitedHealth Group Inc.',
  'HD': 'Home Depot Inc.',
  'DIS': 'Walt Disney Co.',
  'BAC': 'Bank of America Corp.',
  'XOM': 'Exxon Mobil Corp.',
  'PFE': 'Pfizer Inc.',
  'KO': 'Coca-Cola Co.',
  'NFLX': 'Netflix Inc.',
  'AMD': 'Advanced Micro Devices',
  'INTC': 'Intel Corporation',
  'CRM': 'Salesforce Inc.',
  'COIN': 'Coinbase Global Inc.',
  'PLTR': 'Palantir Technologies',
  'SOFI': 'SoFi Technologies Inc.',
  'SPY': 'S&P 500 ETF Trust',
  'QQQ': 'Invesco QQQ Trust',
  'DIA': 'SPDR Dow Jones ETF',
  'IWM': 'iShares Russell 2000 ETF',
  'GLD': 'SPDR Gold Shares',
  'TLT': 'iShares 20+ Year Treasury Bond ETF',
  'VTI': 'Vanguard Total Stock Market ETF',
  'VOO': 'Vanguard S&P 500 ETF',
}

const PAGES: PageResult[] = [
  { path: '/', name: 'Dashboard', category: 'Pages', keywords: ['dashboard', 'home'] },
  { path: '/watchlist', name: 'Watchlist', category: 'Pages', keywords: ['watchlist', 'watch', 'track'] },
  { path: '/screener', name: 'Screener', category: 'Pages', keywords: ['screener', 'screen', 'filter', 'scan'] },
  { path: '/portfolio', name: 'Portfolio', category: 'Pages', keywords: ['portfolio', 'positions', 'holdings'] },
  { path: '/alerts', name: 'Alerts', category: 'Pages', keywords: ['alerts', 'notifications', 'alert'] },
  { path: '/options', name: 'Options Hub', category: 'Pages', keywords: ['options', 'calls', 'puts', 'chain'] },
  { path: '/options-analysis', name: 'Options Analysis', category: 'Pages', keywords: ['options', 'greeks', 'vol'] },
  { path: '/macro', name: 'Macro Dashboard', category: 'Pages', keywords: ['macro', 'economy', 'fed', 'gdp', 'cpi'] },
  { path: '/macro/calendar', name: 'Economic Calendar', category: 'Pages', keywords: ['calendar', 'events', 'fomc', 'nfp'] },
  { path: '/earnings', name: 'Earnings Hub', category: 'Pages', keywords: ['earnings', 'eps', 'revenue', 'results'] },
  { path: '/dividends', name: 'Dividend Hub', category: 'Pages', keywords: ['dividend', 'income', 'yield', 'payout'] },
  { path: '/etf', name: 'ETF Intelligence', category: 'Pages', keywords: ['etf', 'funds', 'index'] },
  { path: '/fixed-income', name: 'Fixed Income', category: 'Pages', keywords: ['bonds', 'treasury', 'fixed', 'income'] },
  { path: '/market-internals', name: 'Market Internals', category: 'Pages', keywords: ['internals', 'breadth', 'advance'] },
  { path: '/market-events', name: 'Market Events', category: 'Pages', keywords: ['events', 'news', 'catalyst'] },
  { path: '/ipo', name: 'IPO & SPAC Tracker', category: 'Pages', keywords: ['ipo', 'spac', 'new', 'listing'] },
  { path: '/charts', name: 'Advanced Charts', category: 'Pages', keywords: ['chart', 'technical', 'tradingview'] },
  { path: '/backtest', name: 'Backtester', category: 'Pages', keywords: ['backtest', 'strategy', 'test', 'historical'] },
  { path: '/strategy-studio', name: 'Strategy Lab', category: 'Pages', keywords: ['strategy', 'lab', 'builder'] },
  { path: '/strategy-leaderboard', name: 'Strategy Leaderboard', category: 'Pages', keywords: ['leaderboard', 'top', 'rank'] },
  { path: '/news', name: 'News & Intel', category: 'Pages', keywords: ['news', 'headlines', 'articles'] },
  { path: '/social', name: 'Social & Alt Data', category: 'Pages', keywords: ['social', 'twitter', 'reddit', 'sentiment'] },
  { path: '/intelligence', name: 'Intelligence', category: 'Pages', keywords: ['intelligence', 'ai', 'insights'] },
  { path: '/scores', name: 'Score Explorer', category: 'Pages', keywords: ['score', 'cipher', 'ranking'] },
  { path: '/settings', name: 'Settings', category: 'Pages', keywords: ['settings', 'account', 'billing', 'preferences'] },
  { path: '/profile', name: 'Profile', category: 'Pages', keywords: ['profile', 'user', 'account'] },
]

const RECENT_SEARCHES_KEY = 'apex_recent_searches'
const MAX_RECENT = 5

interface CommandPaletteProps {
  isOpen?: boolean
  onClose?: () => void
}

export function CommandPalette({ isOpen: controlledOpen, onClose }: CommandPaletteProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen

  const handleSetOpen = useCallback((open: boolean) => {
    if (onClose) {
      if (!open) onClose()
    } else {
      setInternalOpen(open)
    }
  }, [onClose])

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { sendAIPrompt } = useUIStore()

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (stored) {
      setRecentSearches(JSON.parse(stored))
    }
  }, [])

  // Search stocks API (debounced via staleTime)
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['command-search', query],
    queryFn: async () => {
      if (query.length < 2 || query.startsWith('?')) return []
      const response = await apiClient.get(`/search?q=${encodeURIComponent(query)}`)
      return response.data.results as SearchResult[]
    },
    enabled: query.length >= 2 && !query.startsWith('?') && isOpen,
    staleTime: 30000
  })

  // Check if query is an AI query
  const isAIQuery = query.startsWith('?')
  const aiQueryText = isAIQuery ? query.slice(1).trim() : ''

  // Filter pages by query
  const filteredPages = useMemo(() => {
    if (query.length === 0 || isAIQuery) return []
    const q = query.toLowerCase()
    return PAGES.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.path.toLowerCase().includes(q) ||
      p.keywords?.some(kw => kw.includes(q))
    ).slice(0, 5)
  }, [query, isAIQuery])

  // Filter common tickers by query
  const matchedTickers = useMemo(() => {
    if (query.length === 0 || isAIQuery) return []
    const q = query.toUpperCase()
    const matches: { ticker: string; name: string }[] = []

    for (const [ticker, name] of Object.entries(COMMON_TICKERS)) {
      if (ticker.includes(q) || name.toUpperCase().includes(q)) {
        matches.push({ ticker, name })
      }
      if (matches.length >= 6) break
    }

    return matches
  }, [query, isAIQuery])

  // Combine API results with common ticker matches
  const tickerResults = useMemo(() => {
    const results: SearchResult[] = []
    const seen = new Set<string>()

    // Add matched common tickers first
    for (const { ticker, name } of matchedTickers) {
      if (!seen.has(ticker)) {
        results.push({ ticker, company_name: name })
        seen.add(ticker)
      }
    }

    // Add API results
    if (searchResults) {
      for (const result of searchResults) {
        if (!seen.has(result.ticker)) {
          results.push(result)
          seen.add(result.ticker)
        }
      }
    }

    return results.slice(0, 8)
  }, [matchedTickers, searchResults])

  // Combined results
  const allResults = useMemo(() => {
    const results: Array<{
      type: 'ai' | 'page' | 'stock' | 'recent'
      label: string
      sublabel?: string
      path?: string
    }> = []

    // AI query result
    if (isAIQuery && aiQueryText.length > 0) {
      results.push({
        type: 'ai',
        label: `Ask AI: "${aiQueryText}"`,
        sublabel: 'Send to AI Panel'
      })
    }

    // Pages
    for (const page of filteredPages) {
      results.push({
        type: 'page',
        label: page.name,
        sublabel: page.path,
        path: page.path
      })
    }

    // Stocks
    for (const stock of tickerResults) {
      results.push({
        type: 'stock',
        label: stock.ticker,
        sublabel: stock.company_name,
        path: `/stock/${stock.ticker}`
      })
    }

    return results
  }, [isAIQuery, aiQueryText, filteredPages, tickerResults])

  // Recent items when query is empty
  const recentItems = useMemo(() => {
    if (query.length !== 0) return []
    return recentSearches.map(ticker => ({
      type: 'recent' as const,
      label: ticker,
      sublabel: COMMON_TICKERS[ticker] || 'Stock',
      path: `/stock/${ticker}`
    }))
  }, [query, recentSearches])

  const totalResults = allResults.length + recentItems.length

  // Keyboard handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Open palette with Cmd+K or Ctrl+K
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      handleSetOpen(!isOpen)
      return
    }

    // Also open with / when not in input
    if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
      e.preventDefault()
      handleSetOpen(true)
      return
    }

    if (!isOpen) return

    switch (e.key) {
      case 'Escape':
        handleSetOpen(false)
        setQuery('')
        setSelectedIndex(0)
        break
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, totalResults - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        handleSelect(selectedIndex)
        break
    }
  }, [isOpen, totalResults, selectedIndex, handleSetOpen])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  const handleSelect = (index: number) => {
    // Handle recent items
    if (query.length === 0 && index < recentItems.length) {
      const item = recentItems[index]
      if (item.path) {
        addToRecent(item.label)
        navigate(item.path)
      }
      closeAndReset()
      return
    }

    // Adjust index for combined results
    const adjustedIndex = query.length === 0 ? index - recentItems.length : index

    if (adjustedIndex < 0 || adjustedIndex >= allResults.length) return

    const result = allResults[adjustedIndex]

    if (result.type === 'ai') {
      // Send to AI panel
      sendAIPrompt(aiQueryText)
      closeAndReset()
    } else if (result.path) {
      if (result.type === 'stock') {
        addToRecent(result.label)
      }
      navigate(result.path)
      closeAndReset()
    }
  }

  const addToRecent = (ticker: string) => {
    const updated = [ticker, ...recentSearches.filter(t => t !== ticker)].slice(0, MAX_RECENT)
    setRecentSearches(updated)
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
  }

  const closeAndReset = () => {
    handleSetOpen(false)
    setQuery('')
    setSelectedIndex(0)
  }

  if (!isOpen) return null

  return (
    <div className="command-palette-overlay" onClick={() => closeAndReset()}>
      <div className="command-palette" onClick={e => e.stopPropagation()}>
        {/* Search Input */}
        <div className="command-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="command-input"
            placeholder="Search tickers, pages, or type ? to ask AI..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="shortcut-hint">
            <kbd>esc</kbd>
          </div>
        </div>

        {/* Results */}
        <div className="command-results">
          {/* Recent Searches - show when query is empty */}
          {query.length === 0 && recentSearches.length > 0 && (
            <div className="result-group">
              <div className="result-group-header">
                <Clock size={14} />
                Recent
              </div>
              {recentItems.map((item, idx) => (
                <div
                  key={item.label}
                  className={`result-item ${selectedIndex === idx ? 'selected' : ''}`}
                  onClick={() => handleSelect(idx)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <div className="result-icon stock">
                    <TrendingUp size={16} />
                  </div>
                  <div className="result-content">
                    <span className="result-title">{item.label}</span>
                    <span className="result-subtitle">{item.sublabel}</span>
                  </div>
                  <ArrowRight size={14} className="result-arrow" />
                </div>
              ))}
            </div>
          )}

          {/* AI Query */}
          {isAIQuery && aiQueryText.length > 0 && (
            <div className="result-group">
              <div className="result-group-header">
                <Sparkles size={14} />
                AI Assistant
              </div>
              <div
                className={`result-item ai-result ${selectedIndex === 0 ? 'selected' : ''}`}
                onClick={() => handleSelect(0)}
                onMouseEnter={() => setSelectedIndex(0)}
              >
                <div className="result-icon ai">
                  <Sparkles size={16} />
                </div>
                <div className="result-content">
                  <span className="result-title">Ask: "{aiQueryText}"</span>
                  <span className="result-subtitle">Send to AI Panel</span>
                </div>
                <ArrowRight size={14} className="result-arrow" />
              </div>
            </div>
          )}

          {/* Pages */}
          {filteredPages.length > 0 && (
            <div className="result-group">
              <div className="result-group-header">
                <FileText size={14} />
                Pages
              </div>
              {filteredPages.map((page, idx) => {
                const adjustedIdx = isAIQuery && aiQueryText.length > 0 ? idx + 1 : idx
                return (
                  <div
                    key={page.path}
                    className={`result-item ${selectedIndex === adjustedIdx ? 'selected' : ''}`}
                    onClick={() => handleSelect(adjustedIdx)}
                    onMouseEnter={() => setSelectedIndex(adjustedIdx)}
                  >
                    <div className="result-icon page">
                      <FileText size={16} />
                    </div>
                    <div className="result-content">
                      <span className="result-title">{page.name}</span>
                      <span className="result-path">{page.path}</span>
                    </div>
                    <ArrowRight size={14} className="result-arrow" />
                  </div>
                )
              })}
            </div>
          )}

          {/* Stocks */}
          {tickerResults.length > 0 && (
            <div className="result-group">
              <div className="result-group-header">
                <TrendingUp size={14} />
                Stocks
              </div>
              {tickerResults.map((stock, idx) => {
                const baseIdx = (isAIQuery && aiQueryText.length > 0 ? 1 : 0) + filteredPages.length
                const adjustedIdx = baseIdx + idx
                return (
                  <div
                    key={stock.ticker}
                    className={`result-item ${selectedIndex === adjustedIdx ? 'selected' : ''}`}
                    onClick={() => handleSelect(adjustedIdx)}
                    onMouseEnter={() => setSelectedIndex(adjustedIdx)}
                  >
                    <div className="result-icon stock">
                      <TrendingUp size={16} />
                    </div>
                    <div className="result-content">
                      <span className="result-title ticker">{stock.ticker}</span>
                      <span className="result-subtitle">{stock.company_name}</span>
                    </div>
                    {stock.current_price && (
                      <div className="result-meta">
                        <span className="result-price">${stock.current_price.toFixed(2)}</span>
                        {stock.change_pct !== undefined && (
                          <span className={`result-change ${stock.change_pct >= 0 ? 'positive' : 'negative'}`}>
                            {stock.change_pct >= 0 ? '+' : ''}{stock.change_pct.toFixed(2)}%
                          </span>
                        )}
                      </div>
                    )}
                    <ArrowRight size={14} className="result-arrow" />
                  </div>
                )
              })}
            </div>
          )}

          {/* Loading State */}
          {isLoading && query.length >= 2 && !isAIQuery && (
            <div className="command-loading">
              <div className="spinner" />
              Searching...
            </div>
          )}

          {/* Empty State */}
          {!isLoading && query.length > 0 && !isAIQuery && allResults.length === 0 && (
            <div className="command-empty">
              No results for "{query}"
            </div>
          )}

          {/* Quick Tips when empty */}
          {query.length === 0 && recentSearches.length === 0 && (
            <div className="command-tips">
              <p>Start typing to search for stocks or pages</p>
              <div className="tips-grid">
                <div className="tip">
                  <span className="tip-label">Stocks</span>
                  <span className="tip-example">AAPL, MSFT, GOOGL</span>
                </div>
                <div className="tip">
                  <span className="tip-label">Pages</span>
                  <span className="tip-example">Dashboard, Portfolio</span>
                </div>
                <div className="tip">
                  <span className="tip-label">AI Query</span>
                  <span className="tip-example">?what is AAPL's PE ratio</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="command-footer">
          <div className="footer-hint">
            <kbd><CornerDownLeft size={12} /></kbd>
            <span>select</span>
          </div>
          <div className="footer-hint">
            <kbd>↑</kbd><kbd>↓</kbd>
            <span>navigate</span>
          </div>
          <div className="footer-hint">
            <kbd>?</kbd>
            <span>ask AI</span>
          </div>
          <div className="footer-hint">
            <kbd>esc</kbd>
            <span>close</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CommandPalette
